import React, { useCallback, useMemo, useRef, useState } from 'react';
import { usePanelShallowStore } from '@frontend/panel-engine';
import { useNavSize } from '@frontend/responsiveness';
import { StaticSettingsRoutes, useSettingsNavigate } from '@frontend/settings-routing';
import { createContextWithSelectors as createContext, useContextSelector } from '@frontend/store';
import { useNavigate, type AnyRoute, useLocation } from '@frontend/weave-router';
import { Coords, isCoordsInTriangle } from '../utilities/triangle-utils';

/**
 * Shared nav item type – import from nav.tsx if/when that file is refactored to export it.
 * Duplicated here lightly to avoid touching nav.tsx per instructions. Kept minimal.
 */
export type BasicNavItem = {
  root: `/${string}`;
  label: string;
};

export interface NavContextValue {
  open: boolean;
  isDrawerModeActive: boolean;
  isSlimModeActive: boolean;
  currentPathname: string;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  openNav: () => void;
  closeNav: () => void;
  toggleNav: () => void;
  parentLink: string;
  selectedParent: string;
  currentParent: string;
  setCurrentParent: (parent: string) => void;
  isSubMenu: boolean;
  setIsSubMenu: React.Dispatch<React.SetStateAction<boolean>>;
  showSubMenu: () => void;
  showMainMenu: () => void;
  navigate: (path: string) => void;
  isMouseInTriangle: (mouseCoords: Coords) => boolean;
  registerActiveLink: (activeNavLink: HTMLElement) => void;
  subMenuRef: React.RefObject<HTMLDivElement>;
}

const NavContext = createContext<NavContextValue>({} as NavContextValue);

export const NavProvider = ({ children }: { children: React.ReactNode }) => {
  const [open, setOpen] = useState(false);
  const [currentParent, setCurrentParent] = useState<string>('');
  const [isSubMenu, setIsSubMenu] = useState(true);
  const navSize = useNavSize();
  const navigate = useNavigate();
  const { navigate: settingsNavigate } = useSettingsNavigate();
  const { navState } = usePanelShallowStore('navState');
  const currentPath = useLocation().pathname;

  const [, ...pathParts] = currentPath?.split('/') ?? [];
  const selectedParent = `/${pathParts?.[0] ?? ''}`;

  const [prevSelectedParent, setPrevSelectedParent] = useState('');

  const isSlimModeActive = (navSize.isLte('mobile') && navSize.isGt('xsmall')) || navState === 'slim';

  const isDrawerModeActive = navSize.isLte('xsmall');

  const pathNavigate = useCallback(
    (path: string) => {
      if (path.startsWith('#settings')) {
        settingsNavigate({ to: path.replace('#settings', '') as StaticSettingsRoutes });
        return;
      }
      navigate({ to: path as AnyRoute });
    },
    [navigate, settingsNavigate]
  );

  const subMenuRef = useRef<HTMLDivElement>(null);

  const activeLinkCoordsRef = useRef<{ leftRect: DOMRect; rightRect: DOMRect } | null>(null);

  const registerActiveLink = useCallback((activeNavLink: HTMLElement) => {
    if (!subMenuRef.current) {
      return;
    }

    const leftRect = activeNavLink.getBoundingClientRect();
    const rightRect = subMenuRef.current.getBoundingClientRect();

    activeLinkCoordsRef.current = { leftRect, rightRect };
  }, []);

  /**
   * If mouse is in the "safe" triangle, we'll debounce
   * the mouseEnter event, to avoid menu flickering.
   *
   * Outside of the triangle, the mouseEnter event will trigger
   * a menu change immediately.
   */
  const isMouseInTriangle = useCallback((mouseCoords: Coords) => {
    if (!activeLinkCoordsRef.current) {
      return false;
    }

    return isCoordsInTriangle(mouseCoords, activeLinkCoordsRef.current.leftRect, activeLinkCoordsRef.current.rightRect);
  }, []);

  const actualCurrentParent = currentParent || selectedParent;
  const parentLink = actualCurrentParent;

  const currentPathname = currentPath;

  const openNav = useCallback(() => setOpen(true), []);
  const closeNav = useCallback(() => setOpen(false), []);
  const toggleNav = useCallback(() => setOpen((o) => !o), []);
  const showSubMenu = useCallback(() => setIsSubMenu(true), []);
  const showMainMenu = useCallback(() => setIsSubMenu(false), []);

  if (selectedParent !== prevSelectedParent) {
    // Reset the nav parent immediately when navigation changes the active route.
    setPrevSelectedParent(selectedParent);
    setCurrentParent(selectedParent);
  }

  const value: NavContextValue = useMemo(() => {
    return {
      open,
      setOpen,
      openNav,
      closeNav,
      toggleNav,
      currentParent,
      setCurrentParent,
      isSubMenu,
      setIsSubMenu,
      showSubMenu,
      showMainMenu,
      isDrawerModeActive,
      isSlimModeActive,
      navigate: pathNavigate,
      currentPathname,
      parentLink,
      selectedParent,
      isMouseInTriangle,
      registerActiveLink,
      subMenuRef,
      activeLinkCoordsRef,
    };
  }, [
    open,
    openNav,
    closeNav,
    toggleNav,
    currentParent,
    isSubMenu,
    showSubMenu,
    showMainMenu,
    isDrawerModeActive,
    isSlimModeActive,
    pathNavigate,
    currentPathname,
    parentLink,
    selectedParent,
    isMouseInTriangle,
    registerActiveLink,
    subMenuRef,
    activeLinkCoordsRef,
  ]);

  return <NavContext.Provider value={value}>{children}</NavContext.Provider>;
};

export const useNav = <T,>(selector: (state: NavContextValue) => T) => useContextSelector(NavContext, selector);

export default NavProvider;
